/**
 * v-hover 指令
 * 为元素添加 hover 效果
 * 用法: v-hover 或 v-hover="'custom-class-name'" 或 v-hover="{ class: 'custom-class', activeClass: 'active' }"
 */

const vHover = {
  // 在绑定元素的 attribute 或事件监听器被应用之前调用
  created(el, binding) {
    // 保存指令的配置
    const options = getOptions(binding);
    el._hoverState = { options };
  },

  // 在绑定元素的父组件被挂载后调用
  mounted(el, binding) {
    const options = getOptions(binding);
    el._hoverState = { options };

    if (options.disabled) return;

    // 创建事件处理函数
    const addHoverClass = () => {
      el.classList.add(options.class);
      if (options.activeClass) {
        el.classList.add(options.activeClass);
      }
    };

    const removeHoverClass = () => {
      el.classList.remove(options.class);
      if (options.activeClass) {
        el.classList.remove(options.activeClass);
      }
    };

    el.addEventListener("touchstart", addHoverClass);
    el.addEventListener("touchend", removeHoverClass);
    el.addEventListener("touchcancel", removeHoverClass);

    // 存储事件处理函数以便后续清理
    el._hoverState.handlers = {
      addHoverClass,
      removeHoverClass,
    };
  },

  // 在包含组件的 VNode 及其子组件的 VNode 更新后调用
  updated(el, binding) {
    if (binding.oldValue === binding.value) return;

    const prevDisabled = !!el._hoverState?.options?.disabled;
    const nextOptions = getOptions(binding);
    const nextDisabled = !!nextOptions.disabled;

    // 先清理旧监听（如果之前有绑）
    if (el._hoverState?.handlers) {
      const { addHoverClass, removeHoverClass } = el._hoverState.handlers;
      el.removeEventListener("touchstart", addHoverClass);
      el.removeEventListener("touchend", removeHoverClass);
      el.removeEventListener("touchcancel", removeHoverClass);
      delete el._hoverState.handlers;
    }

    el._hoverState = { options: nextOptions };

    // 需要启用才重新绑定
    if (!nextDisabled) vHover.mounted(el, binding);
  },

  // 当指令与元素解除绑定且父组件已卸载时，只调用一次
  unmounted(el) {
    // 确保存在 _hoverState 和 handlers
    if (!el._hoverState?.handlers) return;

    const { addHoverClass, removeHoverClass } = el._hoverState.handlers;

    // 移除所有事件监听器
    el.removeEventListener("mousedown", addHoverClass);
    el.removeEventListener("mouseup", removeHoverClass);
    el.removeEventListener("mouseleave", removeHoverClass);
    el.removeEventListener("touchstart", addHoverClass);
    el.removeEventListener("touchend", removeHoverClass);
    el.removeEventListener("touchcancel", removeHoverClass);

    // 清理状态
    delete el._hoverState;
  },
};

// 辅助函数：解析指令的配置
function getOptions(binding) {
  const defaultOptions = { class: "gelatine", activeClass: "", disabled: false };

  // 显式关闭：v-hover="false"
  if (binding.value === false) return { ...defaultOptions, disabled: true };

  // 无参数：v-hover
  if (binding.value === undefined) return defaultOptions;

  if (typeof binding.value === "string") return { ...defaultOptions, class: binding.value };

  if (typeof binding.value === "object") {
    return {
      ...defaultOptions,
      ...binding.value,
      class: binding.value.class || defaultOptions.class,
      disabled: !!binding.value.disabled,
    };
  }

  return defaultOptions;
}

export default vHover;
